import '../../dashboard/Dashboard.scss';
import React, { useContext, useEffect, useState } from "react";
import { Modal, ModalBody, ModalHeader } from "../../modal/Modal";
import { Trans } from "react-i18next";
import AppContext from "../../../contexts/AppContext";
import { getCurrentYear, isNSCSubdomain } from "../../../utils/Utils";
import Tooltip from "../../tooltip/Tooltip";
import GLOBAL from "../../../global";
import { isMobile } from "react-device-detect";
import { useTenantFeatures } from "../../../features/tenantFeatures/useTenantFeature";

const Footer = () => {
    const { authenticated } = useContext(AppContext);
    const [videoInformationModal, setVideoInformationModal] = useState(false);

    const [isViewportShort, setIsViewportShort] = useState(true);

    const { isAthleteEnabled } = useTenantFeatures();
    const copyRight = (GLOBAL.TENANT_FEATURES?.find(f => f.Name === "CopyRight"));

    const onClickVideoInformationModal = (e) => {
        e && e.preventDefault();
        setVideoInformationModal(!videoInformationModal);
    };

    useEffect(() => {
        let resizeTimer = null;

        const checkViewport = () => {
            try {
                // to prevent the footer from jumping to position:fixed.
                const modalCount = document.body.getAttribute('data-mmh-modal-open-count');
                if (modalCount && parseInt(modalCount, 10) > 0) {
                    return;
                }

                const docHeight = document.documentElement.scrollHeight;
                const winHeight = window.innerHeight;
                setIsViewportShort(docHeight <= winHeight);
            } catch (e) {
                // ignore in non-browser environments
            }
        };

        // Initial check
        checkViewport();

        const onResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(checkViewport, 100);
        };

        window.addEventListener('resize', onResize);

        // Observe DOM changes (data rendering) to re-check when content height changes
        const observer = new MutationObserver(() => {
            // run check in next microtask to allow DOM updates
            Promise.resolve().then(checkViewport);
        });
        observer.observe(document.body, { childList: true, subtree: true, attributes: true });

        return () => {
            window.removeEventListener('resize', onResize);
            observer.disconnect();
            clearTimeout(resizeTimer);
        };
    }, []);

    // Styles
    const fixedStyle = {
        position: 'fixed',
        right: isMobile ? '1rem' : '6rem',
        bottom: isMobile ? '1rem' : '2rem',
        // zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        pointerEvents: 'auto',
        fontSize: isMobile ? "12px" : "14px",
    };

    const staticContainerStyle = {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        boxSizing: 'border-box',
        padding: '0.25rem 1rem',
        marginRight: '0'
    };

    const menuStyle = {
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'flex-end',
        gap: isMobile ? '0.25rem' : '1rem',
        marginRight: isMobile ? '0' : '6rem',
        marginTop: isMobile ? '10px' : '0',
        color: isAthleteEnabled ? 'white' : 'inherit',
        textAlign: 'right'
    };

    const linkStyle = {
        textDecoration: 'none',
        color: 'inherit',
    };

    return (
        <footer>
            {/* Footer: fixed when viewport is taller than content (initial short pages),
                otherwise placed naturally at bottom of page content and aligned to the right. */}
            <div style={isViewportShort ? fixedStyle : staticContainerStyle}>
                <div style={menuStyle}>
                    {!isNSCSubdomain() && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', textAlign: 'right', flexWrap: 'wrap' }}>
                            &copy;{getCurrentYear()}&#x2024;&nbsp;
                            <a
                                style={linkStyle}
                                rel="noopener noreferrer"
                                href={copyRight?.Url}
                                target="_blank">
                                {
                                    <>
                                        {copyRight?.Description}
                                    </>
                                }
                            </a>
                        </div>
                    )}

                    {authenticated && (
                        <>
                            <a
                                className={"text-decoration-none"}
                                rel="noopener noreferrer"
                                href="#!"
                                onClick={onClickVideoInformationModal}
                                id="showVideoInformationUncontrolToolTip">
                                <Trans i18nKey="common.page.tips-for-video" />
                            </a>
                            <Tooltip placement="top" target="showVideoInformationUncontrolToolTip">
                                4 tips for a great video
                            </Tooltip>
                        </>
                    )}
                </div>
            </div>

            <Modal isOpen={videoInformationModal} className="modal-md vide-information-modal">
                <ModalHeader toggle={onClickVideoInformationModal}> Four Tips for a Great Video Experience</ModalHeader>
                <ModalBody>
                    <p><b>1. Use Plenty of Light.</b> Lighting makes a huge difference in the quality of a finished
                        experimental
                        video, so make it one of your top priorities during the experiment.
                        If you don’t use enough properly-placed light, your video will probably look bad. </p>
                    <p><b>2. Use a Clean Background.</b> One easy way to get a measurement result from your video is to
                        use a solid-colored background.
                        A wall, a bedsheet, or a large sheet of backdrop paper are all good options. Make sure your
                        subject stands several feet away from the backdrop to avoid casting shadows on it.</p>
                    <p><b>3. Avoid Shaky Footage.</b> Shaky footage will make any video look like bad.
                        It’s hard to hold a camera completely steady, so try not to hold your camera at all if you can
                        help it. Instead, use a tripod, or set your camera on a sturdy surface.</p>
                    <p><b>4. Follow Instructions.</b> When you will create a video for measurement please follow the
                        instructions properly to get a better measurement angle result.</p>
                    <br />
                    <h6>These tips apply to all participants on the video. </h6>
                </ModalBody>
            </Modal>
        </footer>
    );
};

export default Footer;
